import { useCallback, useState } from 'react';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type {
  ContactFormLeadTriggerObject,
  ContactFormTriggerLocation,
} from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import { emitTrackRequestContactStartEvent } from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import FullscreenOverlay from '../../../ui/FullscreenOverlay';
import { HaloSpokeHeader } from '../HaloSpokeHeader';
import { ContactFormSpoke } from './ContactFormSpoke';

/**
 * Hook that provides a handler to open the contact form from anywhere.
 *
 * Opens ContactFormSpoke in a popup modal (FullscreenOverlay) on all viewports.
 * Caller must render `contactFormOverlay` in their JSX.
 *
 * When a listing is provided, it's forwarded to ContactFormSpoke so the form
 * doesn't depend on Redux's currentListingDetails (which may not be populated
 * outside the HDP).
 */
export const useContactFormAction = (
  listing?: ListingDetails,
  triggerLocation: ContactFormTriggerLocation = 'home_details',
  triggerObject: ContactFormLeadTriggerObject = 'home_details_component|modal_contact_form',
) => {
  const [isOpen, setIsOpen] = useState(false);

  const openContactForm = useCallback(() => {
    emitTrackRequestContactStartEvent({
      listing,
      triggerLocation,
      triggerObject,
    });

    setIsOpen(true);
  }, [listing, triggerLocation, triggerObject]);

  const contactFormOverlay = (
    <FullscreenOverlay.Root open={isOpen} onOpenChange={setIsOpen}>
      <FullscreenOverlay.Portal>
        <FullscreenOverlay.Backdrop>
          <FullscreenOverlay.Content>
            <HaloSpokeHeader title="Contact property" />
            <FullscreenOverlay.Body padding="edge-to-edge">
              <ContactFormSpoke
                onClose={() => setIsOpen(false)}
                listing={listing}
                triggerLocation={triggerLocation}
                triggerObject={triggerObject}
              />
            </FullscreenOverlay.Body>
          </FullscreenOverlay.Content>
        </FullscreenOverlay.Backdrop>
      </FullscreenOverlay.Portal>
    </FullscreenOverlay.Root>
  );

  return { openContactForm, contactFormOverlay };
};
